import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ArrowRight, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatKWD } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { StaffLangToggle } from "@/components/StaffLangToggle";

const PAY = { cash: ["نقداً", "Cash"], knet: ["كي نت", "KNET"] };

export default function Closing() {
  const { logout } = useAuth();
  const { lang, tr } = useLang();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [counted, setCounted] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/staff/closing/today").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return <div className="py-24 text-center text-text-muted">…</div>;

  const diff = counted === "" ? null : (Number(counted) - d.expected_cash);

  const submit = async () => {
    if (busy || d.closed) return;
    if (counted === "") { toast.error(tr("أدخلي مبلغ النقد الفعلي", "Enter the actual cash amount")); return; }
    setBusy(true);
    try {
      await api.post("/staff/closing", { counted_cash: Number(counted), note });
      toast.success(tr("تم اعتماد الإقفال اليومي", "Daily closing approved"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const goBack = () => { if (window.history.state?.idx > 0) nav(-1); else nav("/pos"); };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <header className="bg-[#141110] text-white px-3 py-2 flex items-center justify-between sticky top-0 z-20" style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))" }}>
        <button onClick={goBack} className="flex items-center gap-2 min-h-[44px] px-4 text-base font-medium bg-white text-[#141110] rounded-full" data-testid="closing-back" aria-label={tr("رجوع", "Back")}>{lang === "en" ? <ArrowLeft className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />} {tr("رجوع", "Back")}</button>
        <div className="flex items-center gap-2">
          <StaffLangToggle />
          <button onClick={async () => { await logout(); nav("/login"); }} className="min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label={tr("تسجيل الخروج", "Log out")} data-testid="closing-logout"><LogOut className="w-5 h-5" /></button>
        </div>
      </header>
      <div className="max-w-md mx-auto p-5">
        <h1 className="font-display text-2xl mb-1">{tr("الإقفال اليومي", "Daily closing")}</h1>
        <p className="text-xs text-text-muted mb-5">{d.day}</p>

        <div className="bg-white border border-border p-4 space-y-2 text-sm mb-4" data-testid="closing-summary">
          <div className="flex justify-between"><span>{tr("عدد الفواتير", "Invoices")}</span><span>{d.invoice_count}</span></div>
          {Object.entries(d.by_method).filter(([m]) => m === "cash" || m === "knet").map(([m, v]) => (
            <div key={m} className="flex justify-between"><span>{PAY[m][lang === "en" ? 1 : 0]}</span><span dir="ltr">{formatKWD(v, lang)}</span></div>
          ))}
          <div className="flex justify-between border-t border-border pt-2 font-medium"><span>{tr("النقد المتوقع", "Expected cash")}</span><span dir="ltr" data-testid="closing-expected">{formatKWD(d.expected_cash, lang)}</span></div>
        </div>

        {d.closed ? (
          <div className="bg-green-50 border border-green-200 p-4 text-sm text-green-800" data-testid="closing-done">
            <div className="font-medium mb-1">✓ {tr("تم اعتماد إقفال اليوم بنجاح", "Today's closing has been approved")}</div>
            {tr("النقد الفعلي", "Actual cash")}: <span dir="ltr">{formatKWD(d.closing.counted_cash, lang)}</span> · {tr("الفرق", "Difference")}: <span dir="ltr">{formatKWD(d.closing.difference, lang)}</span>
          </div>
        ) : (
          <div className="space-y-3">
            <div><label className="text-sm font-medium text-[#141110]">{tr("النقد الفعلي في الدرج (بالدينار)", "Actual cash in drawer (KWD)")}</label>
              <input type="number" step="0.001" value={counted} onChange={(e) => setCounted(e.target.value)} data-testid="closing-counted"
                className="w-full mt-1 border border-border px-3 py-2.5 text-sm" /></div>
            {diff !== null && <p className={`text-sm ${Math.abs(diff) < 0.001 ? "text-green-700" : "text-amber-600"}`} data-testid="closing-diff">{tr("الفرق", "Difference")}: <span dir="ltr">{formatKWD(diff, lang)}</span>{Math.abs(diff) >= 0.001 ? tr(" — يوجد فرق، يرجى المراجعة", " — there is a difference, please review") : ""}</p>}
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={tr("ملاحظة (اختياري)", "Note (optional)")} data-testid="closing-note" className="w-full border border-border px-3 py-2 text-sm" />
            <button onClick={submit} disabled={busy} data-testid="closing-submit" className="w-full bg-[#141110] text-white py-3.5 text-base font-medium disabled:opacity-40">{busy ? tr("جارٍ الاعتماد…", "Approving…") : tr("اعتماد إقفال اليوم", "Approve today's closing")}</button>
          </div>
        )}
      </div>
    </div>
  );
}
